import { Route } from '@angular/router';
import { CerFormAdapter } from '@form/cer-form-adapter';
import { CerFormContext } from '@form/cer-form-context';
import { CerFormContextService } from '@form/cer-form-context.service';
import { CerFormDataNavigation } from '@form/cer-form-navigation.service';
import { CerFormView } from '@form/cer-form-view';
import { CerFormViewSet } from '@form/cer-form-view-set';
import { isArray } from '@nevware21/ts-utils';
import { CerUiIcons } from '@ui/cer-ui-icons';
import { UiActionTypeEnum, UiCommand, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { FieldEditChangedArgs, FieldMetaDefaults, FieldMetadata, FieldMetaForeignKey, FieldWidthEnum, UiCommandRenderCell } from 'src/cer/cer-data/cer-data-types';

export const isReviewerParam = 'isReviewer';
export const addToProductRoleParam = 'addToProductRole';

export const uiCommandAddSelectedToProductRole: UiCommand = { id: 'uiCommandAddSelectedToProduct', text: 'Tilknyt markerede', iconCss: CerUiIcons.eye };

export class PartyForm extends CerFormAdapter {
  private isReviewer: boolean = false;
  private addToProductRole: boolean = false;

  //private static tagTemplate: string = '<div ${if(color)}style="background: #${(data.color.toString(16))}"${/if} class="e-chip${if(cls)} ${cls}${/if}"><div class="e-chip-text">${name}</div></div>';

  override viewSetInit(viewSet: CerFormViewSet): void {

    this.isReviewer = this.routeOptions.routeParams?.includes(isReviewerParam)
    this.addToProductRole = Array.isArray(this.routeOptions.callerData) && this.routeOptions.callerData.includes(addToProductRoleParam);


    var view = this.viewGet()
    viewSet.viewAdd(view);
  }

  private viewGet(): CerFormView {

    const view = new CerFormView();

    const dataApiParam: string = this.isReviewer ? "$roleTypeFilter=reviewer" : "";
    view.onCommandEvent = (e => PartyForm.onCommand(e));
    view.viewMetadata = {
      name: 'kontakter', dataApiName: 'Party', dataApiParams: [dataApiParam],
      text: 'Kontakter', textSingular: 'Kontakt', numberSeq: 'Party',
      primaryKey: 'num', baseFields: ['id', 'num', 'name', 'history']
    };

    view.fieldMetadata = PartyForm.partyFieldMetaData();

    if (this.isReviewer) {
      this.reviewerFieldsSetup(view);
    }

    view.fieldGroupMetadata = [
      { idx: 1, width: 200, name: 'identification', text: 'Identifikation', fields: ['id', 'num', 'name', 'attention'] },
      { idx: 2, width: 200, name: 'address', text: 'Adresse', fields: ['address', 'address2', 'country'] },
      { idx: 2, width: 200, name: 'communication', text: 'Kommunikation', fields: ['phone', 'email', 'url'] },
      { idx: 3, width: 200, name: 'setup', text: 'Opsætning', fields: ['publicId', 'publicIdIsCompany', 'legacyId', 'comments', 'tags'] },
      { idx: 3, width: 200, name: 'roles', text: 'Roller', fields: ['isAuthor', 'isPublisher', 'isAgent', 'isVendor', 'isReviewer'] }
    ];

    return view;
  }

  private reviewerFieldsSetup(view: CerFormView) {

    view.viewMetadata.name = 'anmeldere';
    view.viewMetadata.text = this.addToProductRole ? 'Tilknyt anmeldere' : 'Anmeldere';
    view.viewMetadata.textSingular = 'Anmelder';
    const hiddenFields = ['publicId', 'publicIdIsCompany', 'isAuthor', 'isPublisher', 'isAgent', 'isVendor'];
    const defaultField = 'isReviewer';
    view.fieldMetadata.filter(f => hiddenFields.includes(f.name)).forEach(f => {
      f.visible = false; f.visibleAdd = false; f.visibleEdit = false;
    });
    view.fieldMetadata.filter(f => f.name == defaultField).forEach(f => { f.defaultValue = true; f.allowEditOnAdd = false });
    view.listSelectionMode = 'checkboxMulti';
    view.toolbarCommands = [];
    if (this.addToProductRole) {
      view.toolbarCommands.push(uiCommandAddSelectedToProductRole);
      view.listPaging = 'one-page';
    }
  }

  public static partyFieldMetaData(): FieldMetadata[] {
    return [
      { name: 'id', text: 'Kontakt række id', format: 'Integer', visible: false },
      { name: 'num', text: 'Kontakt', allowEdit: false, allowEditOnAdd: false, visibleAdd: false, widthExport: 100 },
      { name: 'name', text: 'Navn', required: true, widthExport: 240 },
      { name: 'attention', text: 'Attention', widthExport: 240 },
      { name: 'address', text: 'Adresse', format: 'TextArea', widthType: FieldWidthEnum.XXXL, widthExport: 240 },
      { name: 'address2', text: 'Postnr/by', widthExport: 240 },
      { fieldType: 'country', visible: false, visibleAdd: true, visibleEdit: true },
      { name: 'publicId', text: 'CVR/CPR' },
      { name: 'publicIdIsCompany', text: 'Er selskab?', format: 'CheckBox', visible: false, visibleAdd: true, visibleEdit: true },
      { fieldType: 'language', visible: false, visibleAdd: false, visibleEdit: false },
      { name: 'phone', text: 'Telefon', format: 'Phone', widthExport: 140 },
      { name: 'email', text: 'E-mail', format: 'Email', widthExport: 240 },
      { name: 'url', text: 'Internet', format: 'UrlBase', visible: false, visibleAdd: true, visibleEdit: true },
      {
        fieldType: 'tags', onRenderCell: PartyForm.onTagsRenderCell, widthType: FieldWidthEnum.XXXXL, widthExport: 200,
        /* template: PartyForm.tagTemplate, */ onTagging: (e => PartyForm.onTagging(e))
      },
      { fieldType: 'comments' },
      { name: 'legacyId', text: 'Oprindelig id', tooltipText: 'Id fra oprindelig database', widthExport: 60 },
      { name: 'isAuthor', text: 'Forfatter', format: 'CheckBox', defaultValue: false },
      { name: 'isPublisher', text: 'Forlag', format: 'CheckBox', defaultValue: false },
      { name: 'isAgent', text: 'Agent', format: 'CheckBox', defaultValue: false },
      { name: 'isVendor', text: 'Kreditor', format: 'CheckBox', defaultValue: false },
      { name: 'isReviewer', text: 'Anmelder', format: 'CheckBox', defaultValue: false }
    ];
  }
  static onTagging(e: any) {
    if (e.itemData?.color && e.chip != null) {
      e.chip.style.background = "#" + e.itemData.color.toString(16);
    }
  }

  static onTagsRenderCell(e: UiCommandRenderCell) {
    const col: any = e.column
    const colName = col?.field;

    var html = "";

    var v: any = e.data;
    if (colName?.length > 0 && v != null) {
      colName.split('.').forEach((c: string) => v = v != null ? v[c] : null);

      if (v != null && isArray(v)) {
        const values = v;
        if (values?.length > 0) {
          const fkData: any[] = (<FieldMetadata> col.meta)?.foreignKey?.data;
          const fkValues = values.map(v => fkData?.find(f => f.id == parseInt(v)));
          if (fkValues?.length > 0) {
            fkValues.filter(fk => fk != null).forEach(fk => html += "<div " + (fk.color != null ? "style='background: #" + fk.color.toString(16) + "'" : "") + " class='e-chip " + /* fk.cls + */ "'><div class='e-chip-text'>" + (fk.shortName ?? fk.name) + "</div></div>");
            html = "<div class='e-chip-list'>" + html + "</div>";
          }
        }
      }
    }
    e.cell.innerHTML = html;
  }

  public static partyFieldMetaDataAsNavigation(partyNavigation: string = 'partyNavigation'): FieldMetadata[] {
    // Get fields / init/expand field type 
    const fieldMetadata = PartyForm.partyFieldMetaData().map(f =>
      FieldMetaForeignKey.init(null, FieldMetaDefaults.init(Object.assign({}, f), true), true)
    );

    // Hide specific fields
    const hiddenFieldName = ['id', 'num', 'legacyId', 'isAuthor', 'isPublisher', 'isAgent', 'isVendor', 'isReviewer'];
    fieldMetadata.filter(f => hiddenFieldName.includes(f.name)).forEach(f => { f.visible = false; f.visibleEdit = false, f.visibleAdd = false });

    // Prefix label on fields 
    const textPrefixNames = ['name', 'attention', 'legacyId', 'created', 'createdBy', 'modified', 'modified'];
    fieldMetadata.filter(f => textPrefixNames.includes(f.name)).forEach(f => f.text = "Kontakt " + f.text.toLocaleLowerCase());

    fieldMetadata.forEach(f => f.name = partyNavigation + '.' + f.name);

    return fieldMetadata;
  }

  public static onCommand(e: UiCommandEvent) {
    PartyForm.onCommandEventFilterChoiceRequest(e);
    switch (e.commandId) {
      case uiCommandAddSelectedToProductRole.id:
        const partyNums = e.appComponent.rowsSelectedData().map((r: any) => r.num);
        if (partyNums.length > 0) {
          e.rowsData = partyNums;
          e.formView = e.formView.parentView;
          e.appComponent = e.formView.appComponent;
          e.formView.triggerCommand(e); // Send to parent view, where logic is defined
        }
    }
  }

  public static partySelectReviewers(parentView: CerFormView, context: CerFormContext) {
    const ctx: CerFormContextService = context.form.formContextService;
    const url = 'project/reviewer';
    const route: Route = ctx.navigationService.routeGetByUrl(url);
    if (route != null) {
      const nav: CerFormDataNavigation = {
        url: url, 
        title: 'Tilføj anmeldere',
        link: 'nav',
        route: route,
        mode: 'select',
        paneTarget: 'main',
        parentView: parentView,
        callerData: [addToProductRoleParam],
        filterStatic: false
      };
      ctx.navigate(nav);
    }
  }

  public static onCommandEventFilterChoiceRequest(e: UiCommandEvent) {
    switch (e.actionType) {
      case UiActionTypeEnum.FilterSearchBegin:
        break;
      case UiActionTypeEnum.FilterChoiceRequest:
        const fieldName: string = e.args.filterModel?.options?.column?.field.replace("partyNavigation.", "");
        if (fieldName == 'tags' || fieldName.endsWith('.tags')) {
          if (e.source == UiCommandSourceEnum.ActionBegin) {
            (e.appComponent as any).grid.on('beforeCheckboxRenderer', PartyForm.onBeforeCheckboxRenderTags, e.appComponent);
            //beforeCheckboxfilterRenderer            
          }
          else {
            (e.appComponent as any).grid.off('beforeCheckboxRenderer', PartyForm.onBeforeCheckboxRenderTags, e.appComponent);
          }
        }
        break;
    }
  }

  public static onBeforeCheckboxRenderTags(args: any) {
    const sbox = (this as any).grid?.filterModule?.filterModule?.excelFilterBase?.sBox;
    if (sbox != null) {
      sbox.parentElement.parentElement.children.item(0).style.display = 'none';
      sbox.getElementsByClassName("e-input-group").item(0).style.display = "none";
    }
    if (args.dataSource?.length > 0) {
      var ids: Set<number> = new Set<number>();
      const fieldNames: string[] = args.field.split('.');
      args.dataSource
        .forEach((r: any) => {
          var tags = r;
          fieldNames.forEach(f => { if (tags != null) { tags = tags[f] } });
          if (tags == null || tags.length == 0) {
            if (!ids.has(null)) {
              ids.add(null);
            }
          }
          else {
            tags.forEach((sId: string) => {
              const id = parseInt(sId);
              if (!ids.has(id) && !isNaN(id)) {
                ids.add(id);
              }
            })
          }
        });
      args.dataSource.length = 0;
      ids.forEach(tagId => {
        var tags: any = {};
        fieldNames.forEach(function f(f, index) { tags[f] = (index < fieldNames.length - 1) ? {} : tagId });
        args.dataSource.push(tags);
      });
    }
  }

  public static partyNumFKChanged(e: FieldEditChangedArgs, navName: string = 'partyNavigation') {
    const party = e.itemData ?? {};
    const row = e.data;
    if (row != null) {
      FieldMetaForeignKey.navigationSetValues(e.editService, row, party, navName);
      var rowOrig = e.editService.getPreviousData();
      if (rowOrig != null) {
        rowOrig[navName] = party;
        e.editService.setPreviousData(rowOrig);
      }
      //e.editService.editedData[navName] = party ;
    }
  }
}
